import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import classNames from 'classnames';
import {
  useRegistrationMutation,
  useLazyConfirmPhoneQuery,
  useSendPhoneConfirmationMutation,
} from 'redux/Api/authApi/authApi';
import s from '../Login/Login.module.scss';
import logo from '../Login/img/logo.svg';

interface LocationState {
  phone?: string;
  surname?: string;
  name?: string;
  patronymic?: string;
  email?: string;
  password?: string;
  isTermsAccepted?: boolean;
  isOrderNotificationsAllowed?: boolean;
  isPromotionalEmailsAllowed?: boolean;
  emailVerified?: boolean;
  fromLogin?: boolean;
}

interface FormData {
  smsCode: string;
}

export const RegistrationStep5: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [verificationError, setVerificationError] = useState('');
  const [isCodeVerified, setIsCodeVerified] = useState(false);
  const [countdown, setCountdown] = useState(59);
  const navigate = useNavigate();
  const location = useLocation();
  const registrationData = location.state as LocationState;

  const [confirmPhone] = useLazyConfirmPhoneQuery();
  const [registerUser] = useRegistrationMutation();
  const [sendPhoneConfirmation] = useSendPhoneConfirmationMutation();

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      smsCode: '',
    },
    mode: 'onChange',
  });

  const smsCode = watch('smsCode');

  useEffect(() => {
    if (!registrationData || !registrationData.emailVerified) {
      navigate('/login');
    }
  }, [registrationData, navigate]);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  const verifySmsCode = async (code: string) => {
    if (!code || code.length !== 6) return false;

    try {
      const result = await confirmPhone({
        phone: registrationData.phone!,
        code: parseInt(code),
      }).unwrap();

      if (result.code_valid) {
        setIsCodeVerified(true);
        setVerificationError('');
        return true;
      } else {
        setVerificationError('Неверный код подтверждения');
        setIsCodeVerified(false);
        return false;
      }
    } catch (error) {
      setVerificationError('Ошибка при проверке кода');
      setIsCodeVerified(false);
      return false;
    }
  };

  // useEffect(() => {
  //   if (smsCode && smsCode.length === 6) {
  //     verifySmsCode(smsCode);
  //   } else {
  //     setIsCodeVerified(false);
  //     setVerificationError('');
  //   }
  // }, [smsCode]);

  const onSubmit = async () => {
    if (!smsCode || smsCode.length !== 6) {
      setVerificationError('Введите код из 6 цифр');
      return;
    }

    setLoading(true);
    setVerificationError('');

    const isValid = await verifySmsCode(smsCode);

    if (!isValid) {
      setLoading(false);
      return;
    }

    try {
      const registrationPayload = {
        name: registrationData.name!,
        surname: registrationData.surname!,
        phone: registrationData.phone!,
        email: registrationData.email!,
        password: registrationData.password!,
        is_terms_accepted: registrationData.isTermsAccepted!,
        is_order_notifications_allowed:
          registrationData.isOrderNotificationsAllowed || false,
        is_promotional_emails_allowed:
          registrationData.isPromotionalEmailsAllowed || false,
        ...(registrationData.patronymic && { patronymic: registrationData.patronymic }),
      };

      const result = await registerUser(registrationPayload).unwrap();

      if (result.id) {
        navigate('/login');
      }
    } catch (error: any) {
      if (error.data?.errors) {
        const errors = error.data.errors;
        if (errors.is_terms_accepted) {
          setVerificationError('Необходимо принять пользовательское соглашение');
        } else if (errors.email) {
          setVerificationError(`Email: ${errors.email[0]}`);
        } else if (errors.phone) {
          setVerificationError(`Телефон: ${errors.phone[0]}`);
        } else {
          const firstError = Object.values(errors)[0];
          setVerificationError(Array.isArray(firstError) ? firstError[0] : firstError);
        }
      } else {
        setVerificationError(error.data?.message || 'Произошла ошибка при регистрации');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBackToStep4 = () => {
    navigate('/registration/step4', { state: registrationData });
  };

  const handleResendSms = async () => {
    try {
      await sendPhoneConfirmation({ phone: registrationData.phone! }).unwrap();
      setCountdown(59);
      setVerificationError('');
    } catch (error: any) {
      const errorMessage =
        error?.data?.message ||
        error?.message ||
        'Ошибка при отправке кода подтверждения';
      setVerificationError(errorMessage);
    }
  };

  const formatPhoneForDisplay = (phone: string) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length >= 11) {
      return `+${digits.slice(0, 1)} (${digits.slice(1, 4)}) ${digits.slice(
        4,
        7,
      )}-${digits.slice(7, 9)}-${digits.slice(9, 11)}`;
    }
    return phone;
  };

  if (!registrationData) {
    return null;
  }

  return (
    <div className={s.enter}>
      <div className={s.inner}>
        <div className={s.logo}>
          <img src={logo} alt='Скилла' />
        </div>

        <h1 className={s.titleStepRegister}>Логин для входа в личный кабинет</h1>
        <div className={s.stepper}>
          <div className={s.stepperItem}></div>
          <div className={s.stepperItem}></div>
          <div className={s.stepperItem}></div>
        </div>

        <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
          <input
            type='tel'
            name='username'
            value={
              registrationData.phone ? formatPhoneForDisplay(registrationData.phone) : ''
            }
            autoComplete='username'
            style={{
              position: 'absolute',
              left: '-9999px',
              opacity: 0,
              pointerEvents: 'none',
            }}
            tabIndex={-1}
            readOnly
          />
          <input
            type='password'
            name='password'
            value={registrationData.password || ''}
            autoComplete='new-password'
            style={{
              position: 'absolute',
              left: '-9999px',
              opacity: 0,
              pointerEvents: 'none',
            }}
            tabIndex={-1}
            readOnly
          />

          <div className={s.inputGroup}>
            <label>Код из СМС</label>
            <input
              type='text'
              placeholder='000 000'
              maxLength={6}
              className={classNames(
                s.input,
                // s.inputOtp,
                (errors?.smsCode?.message || verificationError) && s.input_error,
                isCodeVerified && s.input_success,
              )}
              {...register('smsCode', {
                required: 'Введите код из СМС',
                pattern: {
                  value: /^\d{6}$/,
                  message: 'Код должен содержать 6 цифр',
                },
              })}
              onChange={(e) => {
                const value = e.target.value.replace(/\D/g, '');
                setValue('smsCode', value);
                if (verificationError) {
                  setVerificationError('');
                  setIsCodeVerified(false);
                }
              }}
            />
            {errors?.smsCode?.message && (
              <div className={s.errorMessage}>{errors.smsCode.message}</div>
            )}
            {verificationError && !errors?.smsCode?.message && (
              <div className={s.errorMessage}>{verificationError}</div>
            )}
            {isCodeVerified && <div className={s.successMessage}>Код подтвержден</div>}
          </div>

          <button type='submit' disabled={loading} className={s.submitButton}>
            Завершить регистрацию
          </button>

          <button
            type='button'
            className={s.registerButton}
            onClick={() => navigate('/login')}>
            Начать сначала
          </button>
        </form>

        <div className={s.footer}>
          <span>© {new Date().getFullYear()} go.skilla.ru</span>
        </div>
      </div>
    </div>
  );
};
